import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useTheme } from '../contexts/ThemeContext';
import 'highlight.js/styles/github.css';
import 'highlight.js/styles/github-dark.css';

const DocumentationViewer = ({ 
  documentation, 
  title, 
  iconUrl, 
  iconAlt, 
  category, 
  tags, 
  lastDocumentedAt, 
  documentedBy,
  onEdit,
  showEditButton = false 
}) => {
  const { isDarkMode } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!documentation) {
    return (
      <div className="card" style={{ 
        backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
        border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
        borderRadius: '8px',
        padding: '1.5rem',
        marginBottom: '1rem'
      }}>
        <div style={{ 
          textAlign: 'center', 
          color: isDarkMode ? '#aaa' : '#666',
          fontStyle: 'italic'
        }}>
          <h3 style={{ marginBottom: '0.5rem', color: isDarkMode ? '#ccc' : '#333' }}>
            No Documentation Available
          </h3>
          <p>This {title?.toLowerCase() || 'item'} doesn't have documentation yet.</p>
          {showEditButton && onEdit && (
            <button 
              className="btn btn-primary"
              onClick={onEdit}
              style={{ marginTop: '1rem' }}
            >
              Add Documentation
            </button>
          )}
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseTags = (tagsString) => {
    if (!tagsString) return [];
    return tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
  };

  return (
    <div className="card" style={{ 
      backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
      border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
      borderRadius: '8px',
      padding: '1.5rem',
      marginBottom: '1rem'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between',
        marginBottom: '1rem',
        paddingBottom: '1rem',
        borderBottom: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {iconUrl && (
            <img 
              src={iconUrl} 
              alt={iconAlt || 'Icon'} 
              style={{ 
                width: '48px', 
                height: '48px', 
                borderRadius: '8px',
                objectFit: 'cover'
              }} 
            />
          )}
          <div>
            <h2 style={{ 
              margin: 0, 
              color: isDarkMode ? 'white' : '#333',
              fontSize: '1.5rem'
            }}>
              {title} Documentation
            </h2>
            {category && (
              <span style={{ 
                display: 'inline-block',
                backgroundColor: isDarkMode ? '#4a4a4a' : '#e9ecef',
                color: isDarkMode ? '#e0e0e0' : '#333',
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.875rem',
                marginTop: '0.5rem'
              }}>
                {category}
              </span>
            )}
          </div>
        </div>
        
        {showEditButton && onEdit && (
          <button 
            className="btn btn-secondary"
            onClick={onEdit}
            style={{ 
              padding: '0.5rem 1rem',
              fontSize: '0.875rem'
            }}
          >
            Edit Documentation
          </button>
        )}
      </div>

      {/* Tags */}
      {tags && parseTags(tags).length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {parseTags(tags).map((tag, index) => (
              <span
                key={index}
                style={{
                  backgroundColor: isDarkMode ? '#4a4a4a' : '#e9ecef',
                  color: isDarkMode ? '#e0e0e0' : '#333',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Documentation Content */}
      <div style={{ 
        position: 'relative',
        maxHeight: isExpanded ? 'none' : '400px',
        overflow: 'hidden'
      }}>
        <div 
          className="markdown-content"
          style={{
            color: isDarkMode ? '#e0e0e0' : '#333',
            lineHeight: '1.6',
            fontSize: '1rem'
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            components={{
              // Custom styling for markdown elements
              h1: ({ children }) => (
                <h1 style={{ 
                  color: isDarkMode ? 'white' : '#333',
                  borderBottom: `2px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                  paddingBottom: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 style={{ 
                  color: isDarkMode ? 'white' : '#333',
                  borderBottom: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                  paddingBottom: '0.25rem',
                  marginTop: '1.5rem',
                  marginBottom: '0.75rem'
                }}>
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 style={{ 
                  color: isDarkMode ? 'white' : '#333',
                  marginTop: '1.25rem',
                  marginBottom: '0.5rem'
                }}>
                  {children}
                </h3>
              ),
              code: ({ children, className }) => {
                const isInline = !className;
                return (
                  <code
                    style={{
                      backgroundColor: isDarkMode ? '#3a3a3a' : '#f8f9fa',
                      color: isDarkMode ? '#e0e0e0' : '#333',
                      padding: isInline ? '0.2rem 0.4rem' : '1rem',
                      borderRadius: isInline ? '4px' : '8px',
                      fontSize: isInline ? '0.9em' : '0.875rem',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      display: isInline ? 'inline' : 'block',
                      overflow: 'auto'
                    }}
                    className={className}
                  >
                    {children}
                  </code>
                );
              },
              pre: ({ children }) => (
                <pre style={{
                  backgroundColor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
                  border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                  borderRadius: '8px',
                  padding: '1rem',
                  overflow: 'auto',
                  margin: '1rem 0'
                }}>
                  {children}
                </pre>
              ),
              blockquote: ({ children }) => (
                <blockquote style={{
                  borderLeft: `4px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                  paddingLeft: '1rem',
                  margin: '1rem 0',
                  fontStyle: 'italic',
                  color: isDarkMode ? '#ccc' : '#666'
                }}>
                  {children}
                </blockquote>
              ),
              table: ({ children }) => (
                <div style={{ overflow: 'auto', margin: '1rem 0' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`
                  }}>
                    {children}
                  </table>
                </div>
              ),
              th: ({ children }) => (
                <th style={{
                  backgroundColor: isDarkMode ? '#3a3a3a' : '#f8f9fa',
                  border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: '600'
                }}>
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td style={{
                  border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                  padding: '0.75rem'
                }}>
                  {children}
                </td>
              )
            }}
          >
            {documentation}
          </ReactMarkdown>
        </div>
        
        {/* Fade overlay for collapsed content */}
        {!isExpanded && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '60px',
            background: `linear-gradient(transparent, ${isDarkMode ? '#2a2a2a' : 'white'})`,
            pointerEvents: 'none'
          }} />
        )}
      </div>

      {/* Expand/Collapse Button */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '1rem',
        paddingTop: '1rem',
        borderTop: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`
      }}>
        <button
          className="btn btn-outline"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.875rem'
          }}
        >
          {isExpanded ? 'Show Less' : 'Show More'}
        </button>
      </div>

      {/* Footer */}
      <div style={{ 
        marginTop: '1rem',
        paddingTop: '1rem',
        borderTop: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
        fontSize: '0.875rem',
        color: isDarkMode ? '#aaa' : '#666',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          Last updated: {formatDate(lastDocumentedAt)}
        </div>
        {documentedBy && (
          <div>
            by {documentedBy}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentationViewer;
